import { replay, type Tournament, type TournamentEvent } from '@ctd/core';
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * One stored tournament: its event log, plus a few fields copied from the
 * folded state so the list page does not have to replay every log.
 */
export interface TournamentRecord {
  readonly id: string;
  readonly events: readonly TournamentEvent[];
  readonly updatedAt: string;
  readonly name: string;
  readonly startDate: string;
  readonly system: string;
  readonly playerCount: number;
  readonly roundsPaired: number;
  readonly totalRounds: number;
}

/** Build the stored record for an event log (throws if the log does not replay). */
export function recordFromEvents(
  events: readonly TournamentEvent[],
  updatedAt = new Date().toISOString(),
): TournamentRecord {
  return recordOf(replay(events), events, updatedAt);
}

/** Build the stored record from a log and its already folded state. */
export function recordOf(
  t: Tournament,
  events: readonly TournamentEvent[],
  updatedAt = new Date().toISOString(),
): TournamentRecord {
  return {
    id: t.id,
    events,
    updatedAt,
    name: t.settings.name,
    startDate: t.settings.startDate,
    system: t.settings.system,
    playerCount: t.players.length,
    roundsPaired: t.rounds.length,
    totalRounds: t.settings.rounds,
  };
}

export interface TournamentRepo {
  /** All tournaments, most recently changed first. */
  list(): Promise<TournamentRecord[]>;
  get(id: string): Promise<TournamentRecord | undefined>;
  put(record: TournamentRecord): Promise<void>;
  delete(id: string): Promise<void>;
}

function byUpdated(a: TournamentRecord, b: TournamentRecord): number {
  return b.updatedAt.localeCompare(a.updatedAt) || a.name.localeCompare(b.name);
}

interface CtdDb extends DBSchema {
  tournaments: { key: string; value: TournamentRecord };
}

export const DB_NAME = 'ctd';

/** Tournaments in the browser's IndexedDB, one record per tournament. */
export class IdbRepo implements TournamentRepo {
  #db: Promise<IDBPDatabase<CtdDb>> | null = null;

  constructor(private readonly name = DB_NAME) {}

  #open(): Promise<IDBPDatabase<CtdDb>> {
    this.#db ??= openDB<CtdDb>(this.name, 1, {
      upgrade(db) {
        db.createObjectStore('tournaments', { keyPath: 'id' });
      },
    });
    return this.#db;
  }

  async list(): Promise<TournamentRecord[]> {
    const db = await this.#open();
    return (await db.getAll('tournaments')).sort(byUpdated);
  }

  async get(id: string): Promise<TournamentRecord | undefined> {
    return (await this.#open()).get('tournaments', id);
  }

  async put(record: TournamentRecord): Promise<void> {
    await (await this.#open()).put('tournaments', record);
  }

  async delete(id: string): Promise<void> {
    await (await this.#open()).delete('tournaments', id);
  }

  async close(): Promise<void> {
    if (this.#db !== null) (await this.#db).close();
    this.#db = null;
  }
}

/** In-memory repository (tests, and a fallback when IndexedDB is unavailable). */
export class MemoryRepo implements TournamentRepo {
  readonly records = new Map<string, TournamentRecord>();

  async list(): Promise<TournamentRecord[]> {
    return [...this.records.values()].sort(byUpdated);
  }

  async get(id: string): Promise<TournamentRecord | undefined> {
    return this.records.get(id);
  }

  async put(record: TournamentRecord): Promise<void> {
    this.records.set(record.id, record);
  }

  async delete(id: string): Promise<void> {
    this.records.delete(id);
  }
}
